import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrap, FadeIn } from '../components/Section';

const timeline = [
  { year: '2018', title: 'Foundation', desc: 'Established as a partnership firm with GST registration. Began soil testing operations in Bhopal.' },
  { year: '2019', title: 'NABL Accreditation', desc: 'Received first NABL certificate (TC-8573) for Chemical, Mechanical, and NDT disciplines.' },
  { year: '2022', title: 'Railways Project', desc: 'Partnered with MANIT Bhopal on Indian Railways Mathura–Jhansi slope stability analysis.' },
  { year: '2024', title: 'LLP Incorporation', desc: 'Transitioned to LLP structure (ACE-7707) for corporate contracts and government tenders.' },
  { year: '2024', title: 'New NABL Cycle', desc: 'Received fresh NABL certificate (TC-12248) valid through September 2025.' },
  { year: '2025', title: 'NABL Renewal', desc: 'New NABL accreditation cycle started September 2025, valid through September 2029.' },
];

export default function About() {
  return (
    <PageWrap>
      {/* Hero */}
      <section style={{ padding: '160px 0 80px', background: 'var(--warm)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--primary)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>About Us</p>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 20 }}>
              Building Trust Through<br /><span style={{ color: 'var(--primary-light)' }}>Precision & Accreditation</span>
            </h1>
            <p style={{ fontSize: '1.08rem', color: 'var(--text-mid)', maxWidth: 650, lineHeight: 1.7 }}>
              From a Bhopal-based soil testing lab to Central India's leading LLP-structured geotechnical consultancy — with NABL accreditation, academic partnerships, and national infrastructure project experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Leadership */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn><h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>Leadership</h2><p style={{ color: 'var(--text-light)', marginBottom: 48 }}>Experienced professionals driving technical excellence.</p></FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
            {[
              { initials: 'DRB', name: 'D.R. Bharti', role: 'Partner & Director', desc: 'Retired Railway officer with extensive experience executing national railway infrastructure projects. Leads business development, corporate strategy, and government tender management.' },
              { initials: 'SLN', name: 'Shadi Lal Narwaria', role: 'Partner & Director', desc: 'Retired Railway officer with deep expertise in geotechnical and civil engineering projects. Oversees all laboratory operations, NABL compliance, quality management, and research partnerships.' },
            ].map((p, i) => (
              <FadeIn key={p.name} delay={i * 0.1}>
                <div style={{ padding: 40, background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
                  <div style={{ width: 70, height: 70, margin: '0 auto 20px', background: 'var(--primary)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Playfair Display',serif", fontSize: '1.1rem', fontWeight: 700 }}>{p.initials}</div>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: 4 }}>{p.name}</h3>
                  <span style={{ fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 500 }}>{p.role}</span>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', marginTop: 16, lineHeight: 1.6 }}>{p.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ padding: '100px 0', background: 'var(--cream)' }}>
        <div className="container">
          <FadeIn><h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>Our Journey</h2><p style={{ color: 'var(--text-light)', marginBottom: 48 }}>Nine years of consistent growth and national project delivery.</p></FadeIn>
          <div style={{ maxWidth: 700, margin: '0 auto' }}>
            {timeline.map((t, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div style={{ display: 'flex', gap: 24, padding: '24px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.1rem', fontWeight: 600, color: 'var(--primary)', width: 60, flexShrink: 0 }}>{t.year}</div>
                  <div>
                    <h4 style={{ fontSize: '1.05rem', marginBottom: 6 }}>{t.title}</h4>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>{t.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditation */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 60, alignItems: 'start' }}>
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 16 }}>NABL Accreditation</h2>
            <p style={{ fontSize: '0.98rem', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 16 }}>
              The cornerstone of Hicon's credibility is continuous accreditation by NABL, ensuring compliance with ISO/IEC 17025:2017. Our results are legally accepted by municipal authorities, structural engineers, and auditors.
            </p>
            <p style={{ fontSize: '0.98rem', color: 'var(--text-mid)', lineHeight: 1.7 }}>
              Continuous certification across multiple renewal cycles demonstrates operational stability and technical competence.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', background: 'var(--primary-dark)', color: 'white', fontSize: '0.9rem', fontWeight: 500 }}>Current Certificate</div>
              <div style={{ padding: 24 }}>
                {[
                  ['Certificate No.', 'TC-12248'],
                  ['Standard', 'ISO/IEC 17025:2017'],
                  ['Disciplines', 'Mechanical, Chemical, NDT'],
                  ['Issue Date', 'February 11, 2024'],
                  ['Valid Until', 'September 2029'],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)', fontSize: '0.88rem' }}>
                    <span style={{ color: 'var(--text-light)' }}>{label}</span>
                    <strong style={{ color: 'var(--primary-dark)' }}>{value}</strong>
                  </div>
                ))}
              </div>
              <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <a href="/documents/nabl-certificate.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Download NABL Certificate</a>
                <a href="/documents/iso-certificate.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }}>Download ISO Certificate</a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Corporate Network */}
      <section style={{ padding: '100px 0', background: 'var(--warm)' }}>
        <div className="container">
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>Our Clients & Network</h2>
            <p style={{ color: 'var(--text-light)', marginBottom: 32 }}>Trusted by leading infrastructure organizations and government departments across India.</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 40 }}>
              {['Indian Railways', 'L&T', 'Power Grid Corporation', 'KEC International', 'KTPL International', 'Rahee Infra', 'NHAI', 'PWD', 'WRD & Irrigation Dept.', 'MPRDA', 'MPRRDA'].map(p => (
                <span key={p} style={{ padding: '12px 20px', background: 'white', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.88rem', color: 'var(--text-mid)' }}>{p}</span>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-mid)', maxWidth: 500 }}>The LLP structure enables Hicon to bid on major public tenders issued by CPWD, NHAI, and Indian Railways with limited partner liability.</p>
              <Link to="/contact" className="btn btn-primary">Partner With Us</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageWrap>
  );
}
